import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  ListSubheader,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { measurementsApi } from "@api/measurements";
import { useAppSelector, useAppDispatch } from "@hooks/redux";
import { fetchApiariesAndGroups } from "@store/slices/locationsSlice";
import MeasurementsChart from "@components/measurements/MeasurementsChart";
import {
  format,
  subDays,
  subMonths,
  subYears,
  startOfDay,
  endOfDay,
} from "date-fns";
import { Hive } from "@types";

interface PeriodOption {
  label: string;
  value: string;
  interval: string;
  getDates: () => { start: Date; end: Date };
}

const Measurements: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { id: hiveId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();
  const { devices } = useAppSelector((state) => state.devices);
  const { apiaries, hives } = useAppSelector((state) => state.locations);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<any>({});
  const [selectedPeriod, setSelectedPeriod] = useState("day");
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [relativeStartpoint, setRelativeStartpoint] = useState(false);
  const [selectedHive, setSelectedHive] = useState<string>(hiveId || "");
  const [hasInitializedDefaultHive, setHasInitializedDefaultHive] = useState(false);

  const periods: PeriodOption[] = [
    {
      label: "Today",
      value: "day",
      interval: "hour",
      getDates: () => ({
        start: new Date(new Date().setHours(0, 0, 0, 0)),
        end: new Date(),
      }),
    },
    {
      label: "Week",
      value: "week",
      interval: "day",
      getDates: () => ({
        start: subDays(new Date(), 7),
        end: new Date(),
      }),
    },
    {
      label: "Month",
      value: "month",
      interval: "day",
      getDates: () => ({
        start: subMonths(new Date(), 1),
        end: new Date(),
      }),
    },
    {
      label: "3 Months",
      value: "3months",
      interval: "week",
      getDates: () => ({
        start: subMonths(new Date(), 3),
        end: new Date(),
      }),
    },
    {
      label: "Year",
      value: "year",
      interval: "month",
      getDates: () => ({
        start: subYears(new Date(), 1),
        end: new Date(),
      }),
    },
    {
      label: "Custom",
      value: "custom",
      interval: "day",
      getDates: () => ({
        start: customStartDate || subDays(new Date(), 7),
        end: customEndDate || new Date(),
      }),
    },
  ];

  const currentPeriod =
    periods.find((p) => p.value === selectedPeriod) || periods[0];

  useEffect(() => {
    if (devices.length > 0) {
      loadMeasurements();
    }
  }, [
    selectedPeriod,
    selectedHive,
    customStartDate,
    customEndDate,
    relativeStartpoint,
    devices,
  ]);
  
  useEffect(() => {
    if (apiaries.length === 0) {
      dispatch(fetchApiariesAndGroups());
    } else if (!hasInitializedDefaultHive && !selectedHive) {
      // Find the first hive in the first apiary that has hives
      for (const apiary of apiaries) {
        if (apiary.hives && apiary.hives.length > 0) {
          setSelectedHive(apiary.hives[0].id.toString());
          break;
        }
      }
      setHasInitializedDefaultHive(true);
    } else if (apiaries.length > 0) {
      setHasInitializedDefaultHive(true);
    }
  }, [dispatch, apiaries, hasInitializedDefaultHive, selectedHive]);

  const loadMeasurements = async () => {
    if (devices.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const { start, end } = currentPeriod.getDates();

      const params = {
        start: format(start, "yyyy-MM-dd HH:mm:ss"),
        end: format(end, "yyyy-MM-dd HH:mm:ss"),
        interval: currentPeriod.interval,
        ...(selectedHive && { hive_id: parseInt(selectedHive) }),
        group_by: 'apiary',
        limit: 1000,
      };

      const response = selectedHive
        ? await measurementsApi.getHiveMeasurements(
            parseInt(selectedHive),
            params,
          )
        : await measurementsApi.getMeasurements(params);

      setMeasurements(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load measurements");
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  if (devices.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, mt: 2 }}>
          <Button
            variant="outlined"
            sx={{
              color: "#F8B133",
              borderColor: "#F8B133",
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "0.875rem",
              px: 3,
              py: 1,
              "&:hover": {
                borderColor: "#F8B133",
                backgroundColor: "rgba(248, 177, 51, 0.08)",
              },
            }}
            onClick={() => navigate("/devices")}
          >
            ✏️ EDIT DEVICES
          </Button>
        </Box>
        <Box sx={{ textAlign: "left", py: 4, px: 2 }}>
          <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
            Devices not yet available. Click on the button in the upper right
            corner to add one.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <img
              src="/img/SmartNyuki-base-small.png"
              alt="Smart Nyuki Base Device"
              style={{ maxWidth: "100%", width: 600 }}
            />
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ lineHeight: 1.6 }}
          >
            If you have a Smart Nyuki base (shown in the picture above), please
            use the Smart Nyuki base app (iOS and Android) to set-up the
            communication with this app. If you don't have a Smart Nyuki base
            yet, please click on the menu item 'Smart Nyuki website' for updates
            on how to get a Smart Nyuki base. If you have your own measurement
            device and would like to see the data in the Smart Nyuki app, please
            send us a message to ask for joining our Slack community and you can
            get access to the API description.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, mt: 2 }}>
          <Button
            variant="outlined"
            sx={{
              color: "#F8B133",
              borderColor: "#F8B133",
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "0.875rem",
              px: 3,
              py: 1,
              "&:hover": {
                borderColor: "#F8B133",
                backgroundColor: "rgba(248, 177, 51, 0.08)",
              },
            }}
            onClick={() => navigate("/devices")}
          >
            ✏️ EDIT DEVICES
          </Button>
        </Box>

        {/* Period Selection Bar */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0",
            py: 1.5,
            mb: 3,
            borderRadius: 1,
          }}
        >
          <Box>
            {isMobile ? (
              // Mobile layout - stacked (matching SmartNyuki Vue app)
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {periods.slice(0, -2).map((period) => (
                    <Button
                      key={period.value}
                      size="small"
                      variant={
                        selectedPeriod === period.value ? "contained" : "text"
                      }
                      onClick={() => handlePeriodChange(period.value)}
                      sx={{
                        minWidth: "auto",
                        px: 1,
                        py: 0.5,
                        fontSize: "0.75rem",
                        color:
                          selectedPeriod === period.value
                            ? "white"
                            : "text.secondary",
                      }}
                    >
                      {period.label}
                    </Button>
                  ))}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    py: 0.5,
                  }}
                >
                  {periods.slice(-2).map((period) => (
                    <Button
                      key={period.value}
                      size="small"
                      variant={
                        selectedPeriod === period.value ? "contained" : "text"
                      }
                      onClick={() => handlePeriodChange(period.value)}
                      sx={{
                        minWidth: 60,
                        fontSize: "0.75rem",
                        color:
                          selectedPeriod === period.value
                            ? "white"
                            : "text.secondary",
                      }}
                    >
                      {period.label}
                    </Button>
                  ))}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={relativeStartpoint}
                        onChange={(e) =>
                          setRelativeStartpoint(e.target.checked)
                        }
                        disabled={selectedPeriod === "custom"}
                        size="small"
                      />
                    }
                    label="Relative"
                    sx={{
                      ml: 1,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                </Box>
              </Box>
            ) : (
              // Desktop layout - horizontal (matching SmartNyuki Vue app)
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  {periods.map((period) => (
                    <Button
                      key={period.value}
                      variant={
                        selectedPeriod === period.value ? "contained" : "text"
                      }
                      onClick={() => handlePeriodChange(period.value)}
                      sx={{
                        color:
                          selectedPeriod === period.value
                            ? "white"
                            : "text.secondary",
                        "&:hover": {
                          backgroundColor:
                            selectedPeriod === period.value
                              ? "primary.dark"
                              : "action.hover",
                        },
                      }}
                    >
                      {period.label}
                    </Button>
                  ))}
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={relativeStartpoint}
                      onChange={(e) => setRelativeStartpoint(e.target.checked)}
                      disabled={selectedPeriod === "custom"}
                    />
                  }
                  label="Relative Startpoint"
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ pb: 4 }}>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Hive</InputLabel>
                    <Select
                      value={selectedHive}
                      onChange={(e) => setSelectedHive(e.target.value)}
                      label="Hive"
                    >
                      <MenuItem value="">All Hives</MenuItem>
                      {apiaries.map((apiary) => [
                        <ListSubheader 
                          key={`apiary-${apiary.id}`}
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main', 
                            bgcolor: 'background.paper',
                            lineHeight: '36px',
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.1em'
                          }}
                        >
                          {apiary.name}
                        </ListSubheader>,
                        ...(apiary.hives || []).map((hive) => (
                          <MenuItem key={hive.id} value={hive.id.toString()} sx={{ pl: 4 }}>
                            {hive.name}
                          </MenuItem>
                        ))
                      ])}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedPeriod === "custom" && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <DatePicker
                        label="Start Date"
                        value={customStartDate}
                        onChange={setCustomStartDate}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <DatePicker
                        label="End Date"
                        value={customEndDate}
                        onChange={setCustomEndDate}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Charts Grouped by Apiary (Pre-structured by Backend) */}
          {!loading && !error && Object.keys(measurements).length > 0 && (
            <Box>
              {Object.entries(measurements).map(([apiaryName, apiaryMeasurements]: [string, any]) => (
                <Box key={apiaryName} sx={{ mb: 6 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 3, 
                      pb: 1, 
                      borderBottom: '2px solid', 
                      borderColor: 'primary.main',
                      width: 'fit-content',
                      fontWeight: 'bold',
                      color: 'text.primary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em'
                    }}
                  >
                    📍 {apiaryName}
                  </Typography>
                  <Grid container spacing={3}>
                    {Object.entries(
                      apiaryMeasurements.reduce((acc: any, m: any) => {
                        let type = m.sensor_type || m.abbreviation || 'unknown';
                        let groupKey = type;
                        if (type.startsWith('t')) groupKey = 'Temperature';
                        if (type.startsWith('h')) groupKey = 'Humidity';
                        if (type.startsWith('w')) groupKey = 'Weight';
                        if (type === 's') groupKey = 'Sound';
                        if (type === 'battery_voltage') groupKey = 'Battery';
                        if (type === 'signal_strength' || type === 'signal_noise') groupKey = 'Connectivity';

                        if (!acc[groupKey]) acc[groupKey] = [];
                        acc[groupKey].push(m);
                        return acc;
                      }, {})
                    ).map(([groupName, groupMeasurements]: [string, any]) => (
                      <Grid item xs={12} md={6} lg={groupName === 'Sound' ? 12 : 6} key={groupName}>
                        <MeasurementsChart
                          data={groupMeasurements}
                          interval={currentPeriod.interval}
                          title={groupName}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Box>
          )}

          {/* No Data */}
          {!loading && !error && Object.keys(measurements).length === 0 && (
            <Card>
              <CardContent sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No measurement data available
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Try selecting a different time period or check if your devices
                  are connected.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default Measurements;
